import React, { useState, useEffect } from 'react';
import { 
  Fab, Box, Typography, Grid, Button, IconButton, Paper 
} from '@mui/material';
import { Calculate, Close, Backspace, DragIndicator } from '@mui/icons-material';

function FloatingCalculator() {
  const [open, setOpen] = useState(false);
  const [expressao, setExpressao] = useState('');
  const [historico, setHistorico] = useState([]);

  // Lógica de Arrastar NATIVA do React
  const [posicao, setPosicao] = useState({ x: 20, y: 80 });
  const [isDragging, setIsDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Centraliza no canto inferior direito ao abrir pela 1ª vez
  useEffect(() => {
    setPosicao({ x: window.innerWidth > 400 ? window.innerWidth - 350 : 10, y: 100 });
  }, []);

  const handlePointerDown = (e) => {
    setIsDragging(true);
    setOffset({ x: e.clientX - posicao.x, y: e.clientY - posicao.y });
    e.target.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (isDragging) setPosicao({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    e.target.releasePointerCapture(e.pointerId);
  };

  const handleClick = (valor) => {
    const ultimo = expressao.slice(-1);
    if (['+', '-', 'x', '÷', '.'].includes(valor) && ['+', '-', 'x', '÷', '.'].includes(ultimo)) return;
    setExpressao(prev => prev + valor);
  };

  const calcular = () => {
    if (!expressao) return;
    try {
      // Traduz os símbolos visuais para JS
      let calcStr = expressao
        .replace(/x/g, '*')
        .replace(/÷/g, '/')
        .replace(/sin\(/g, 'Math.sin(')
        .replace(/cos\(/g, 'Math.cos(')
        .replace(/tan\(/g, 'Math.tan(')
        .replace(/√\(/g, 'Math.sqrt(')
        .replace(/\^2/g, '**2');

      // eslint-disable-next-line no-new-func
      const resultado = new Function('return ' + calcStr)();
      const resFormatado = String(Math.round(resultado * 100000000) / 100000000);
      
      setHistorico(prev => [...prev.slice(-3), `${expressao} = ${resFormatado}`]);
      setExpressao(resFormatado);
    } catch (e) {
      setExpressao('Erro');
      setTimeout(() => setExpressao(''), 1000);
    }
  };

  return (
    <>
      {/* Botão Flutuante */}
      <Fab 
        color="primary" 
        onClick={() => setOpen(!open)}
        sx={{ 
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999, 
          background: 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)',
          boxShadow: '0 4px 12px rgba(26, 35, 126, .4)'
        }}
      >
        <Calculate />
      </Fab>

      {/* A Janela Flutuante */}
      <Paper 
        elevation={12}
        sx={{ 
          position: 'fixed', left: posicao.x, top: posicao.y, zIndex: 9999, 
          width: 320, bgcolor: '#0f172a', borderRadius: 4, overflow: 'hidden',
          display: open ? 'block' : 'none', border: '1px solid rgba(255,255,255,0.15)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
        }}
      >
        {/* Cabeçalho de Arrastar */}
        <Box 
          onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}
          sx={{ bgcolor: '#1e293b', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
        >
          <Typography variant="caption" sx={{ color: '#00e676', display: 'flex', alignItems: 'center', fontWeight: 'bold', letterSpacing: 1 }}>
            <DragIndicator fontSize="small" sx={{ mr: 0.5 }} /> LNT TECH CALCULATOR
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#ff5252', p: 0 }}><Close fontSize="small"/></IconButton>
        </Box>
        
        <Box sx={{ p: 2 }}>
          {/* Visor */}
          <Paper sx={{ mb: 2, p: 1.5, bgcolor: '#1e293b', borderRadius: 3, textAlign: 'right', minHeight: 70, border: '1px solid #334155' }}>
            {historico.length > 0 && <Typography variant="caption" display="block" color="#64748b">{historico[historico.length - 1]}</Typography>}
            <Typography variant="h5" color="#00e676" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>{expressao || '0'}</Typography>
          </Paper>

          {/* Botões Científicos */}
          <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
            {['sin(', 'cos(', 'tan(', '√(', '^2'].map(btn => (
              <Grid item xs={2.4} key={btn}>
                <Button fullWidth variant="contained" onClick={() => handleClick(btn)} sx={{ height: 35, minWidth: 0, bgcolor: '#334155', color: '#94a3b8', fontSize: '0.7rem', '&:hover': { bgcolor: '#475569' } }}>{btn.replace('(', '')}</Button>
              </Grid>
            ))}
          </Grid>

          {/* Botões de Ação */}
          <Grid container spacing={0.5} sx={{ mb: 0.5 }}>
            <Grid item xs={3}><Button fullWidth variant="contained" onClick={() => {setExpressao(''); setHistorico([]);}} sx={{ height: 45, bgcolor: '#ef4444', fontWeight: 'bold', '&:hover': { bgcolor: '#dc2626' } }}>C</Button></Grid>
            <Grid item xs={3}><Button fullWidth variant="contained" onClick={() => setExpressao(prev => prev.slice(0, -1))} sx={{ height: 45, bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}><Backspace fontSize="small"/></Button></Grid>
            <Grid item xs={3}><Button fullWidth variant="contained" onClick={() => handleClick('(')} sx={{ height: 45, bgcolor: '#334155', '&:hover': { bgcolor: '#475569' } }}>(</Button></Grid>
            <Grid item xs={3}><Button fullWidth variant="contained" onClick={() => handleClick(')')} sx={{ height: 45, bgcolor: '#334155', '&:hover': { bgcolor: '#475569' } }}>)</Button></Grid>
          </Grid>

          {/* Teclado Numérico */}
          <Grid container spacing={0.5}>
            {['7','8','9','÷', '4','5','6','x', '1','2','3','-', '0','.','=','+'].map((btn) => (
              <Grid item xs={3} key={btn}>
                <Button 
                  fullWidth variant="contained" 
                  onClick={() => btn === '=' ? calcular() : handleClick(btn)}
                  sx={{ 
                    height: 50, fontSize: '1.2rem', fontWeight: 'bold', minWidth: 0, 
                    bgcolor: ['÷', 'x', '-', '+'].includes(btn) ? '#3b82f6' : btn === '=' ? '#10b981' : '#475569',
                    '&:hover': { bgcolor: ['÷', 'x', '-', '+'].includes(btn) ? '#2563eb' : btn === '=' ? '#059669' : '#64748b' }
                  }}
                >
                  {btn}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    </>
  );
}

export default FloatingCalculator;
